'use client';

import React from 'react';
import { ArrowLeft } from 'react-feather';

import {
  Box,
  Button,
  Circle,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from '@/components';
import { useExclusiveLink, useShare, useTranslation } from '@/hooks';
import UserTeam from '@/svgs/dashboard/drawer/UsersThree.svg';
import UserIcon from '@/svgs/User.svg';

type ReferralLinkType = 'agent' | 'customer';

type ReferralLinkModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const createReferralLink = (link: string, type: ReferralLinkType): string => {
  const url = new URL(link, window.location.origin);
  url.searchParams.set('type', type);
  return url.toString();
};

const ReferralLinkModal: React.FC<ReferralLinkModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation('components.dashboard.drawer');
  const { refetch } = useExclusiveLink();
  const handleShare = useShare();
  const [loadingType, setLoadingType] = React.useState<ReferralLinkType | null>(
    null
  );

  const shareReferralLink = async (type: ReferralLinkType) => {
    setLoadingType(type);
    try {
      const { data: response } = await refetch();
      if (!response?.link) {
        return;
      }

      await handleShare({
        title: t('exclusiveLinkCopied'),
        url: createReferralLink(response.link, type),
      });
      onClose();
    } finally {
      setLoadingType(null);
    }
  };

  const ReferralLinkOption = ({
    type,
    title,
  }: {
    type: ReferralLinkType;
    title: string;
  }) => {
    const Icon = type === 'agent' ? UserTeam : UserIcon;

    return (
      <Button
        variant="bordered"
        minH="76px"
        w="full"
        justifyContent="space-between"
        px={4}
        isLoading={loadingType === type}
        onClick={() => void shareReferralLink(type)}
      >
        <HStack gap={3}>
          <Circle
            size="44px"
            p={2}
            bg={type === 'agent' ? 'secondary.100' : 'primary.50'}
            sx={{
              stroke: 'text',
            }}
          >
            <Icon />
          </Circle>
          <Text fontSize="md" fontWeight={700}>
            {title}
          </Text>
        </HStack>
        <Circle size="32px">
          <ArrowLeft size={18} />
        </Circle>
      </Button>
    );
  };

  return (
    <Modal isCentered isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent
        maxWidth={{ base: 'calc(100dvw - 32px)', md: '460px' }}
        borderRadius="18px"
        overflow="hidden"
      >
        <Box bg="primary.500" px={6} pt={8} pb={7} color="white">
          <ModalHeader p={0} fontSize="xl">
            {t('referralLinkModalTitle')}
          </ModalHeader>
        </Box>
        <ModalCloseButton sx={{ '&  path': { stroke: 'white' } }} />
        <ModalBody px={5} py={6} bg="gray.50">
          <Stack gap={3}>
            <ReferralLinkOption
              type="customer"
              title={t('customerReferralLink')}
            />
            <ReferralLinkOption type="agent" title={t('agentReferralLink')} />
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ReferralLinkModal;
